/**
 * The player's own ICE configuration, persisted in the browser.
 *
 * STUN is a fixed default (`DEFAULT_STUN_URLS`) because a public STUN server
 * costs its operator almost nothing and needs no account. TURN is not: it
 * relays the whole connection, so it is somebody's bandwidth bill, and this
 * port ships the *field* rather than a server. Anyone behind a symmetric NAT
 * pastes their own relay's URL and credentials here and the duel works; anyone
 * who does not need one never sees a difference.
 *
 * Same storage-agnostic shape as `audio/settings.ts`, and for the same reason:
 * `bun test` has no `localStorage`, and the round-trip is worth testing.
 */

import { DEFAULT_ICE, TURN_STORAGE_KEY, parseTurnConfig, serializeTurnConfig, type IceConfig, type TurnConfig } from "@red-opus/net";
import type { SettingsStore } from "../audio/settings.ts";

export const EMPTY_TURN: TurnConfig = { url: "", username: "", credential: "" };

/** The stored relay, or `null` when none is configured (or the stored value is unusable). */
export function loadTurnConfig(store: SettingsStore | undefined): TurnConfig | null {
  return store ? parseTurnConfig(store.getItem(TURN_STORAGE_KEY)) : null;
}

/**
 * Writes the relay, or clears it. A `url` the parser will not accept is
 * treated as "clear" rather than stored and silently ignored later — the one
 * behaviour that cannot leave the lobby claiming a relay it will not use.
 */
export function saveTurnConfig(store: SettingsStore | undefined, turn: TurnConfig): TurnConfig | null {
  if (!store) return null;
  const usable = parseTurnConfig(serializeTurnConfig(turn));
  store.setItem(TURN_STORAGE_KEY, usable ? serializeTurnConfig(usable) : "");
  return usable;
}

export function iceConfigFrom(turn: TurnConfig | null): IceConfig {
  return { ...DEFAULT_ICE, turn };
}
